import { isEmpty, omit } from 'lodash';
import moment from 'moment';

const days = [1, 2, 3, 4, 5, 6, 7] as const;
const hours = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
  22, 23
] as const;
export type DaypartsDay = (typeof days)[number];
export type DaypartsHour = (typeof hours)[number];
export type Dayparts = {
  [Day in DaypartsDay]: DaypartsHour[];
} & { enabled: boolean };

export const isDayOfDaypart = (dayValue: number): dayValue is DaypartsDay => {
  return dayValue >= 1 && dayValue <= 7;
};

export const isHourOfDaypart = (
  hourValue: number
): hourValue is DaypartsHour => {
  return hourValue >= 0 && hourValue <= 23;
};

export function getInitDaypart(): Dayparts {
  return {
    1: Array.from({ length: 24 }, (_, index) => index) as DaypartsHour[],
    2: Array.from({ length: 24 }, (_, index) => index) as DaypartsHour[],
    3: Array.from({ length: 24 }, (_, index) => index) as DaypartsHour[],
    4: Array.from({ length: 24 }, (_, index) => index) as DaypartsHour[],
    5: Array.from({ length: 24 }, (_, index) => index) as DaypartsHour[],
    6: Array.from({ length: 24 }, (_, index) => index) as DaypartsHour[],
    7: Array.from({ length: 24 }, (_, index) => index) as DaypartsHour[],
    enabled: true
  };
}

export function getDaypartDescriptions(
  startTime: string,
  endTime: string,
  daypart?: Omit<Dayparts, 'enabled'>
): string[] {
  let targetDaypart = daypart;
  if (isEmpty(daypart) || targetDaypart === undefined) {
    targetDaypart = omit(getInitDaypart(), 'enabled');
  }
  const start = moment(startTime);
  const startString = start.format('YYYY-MM-DD');
  const startDate = moment(startTime).startOf('day');
  const end = moment(endTime);
  const endString = end.format('YYYY-MM-DD');
  const endDate = moment(endTime).startOf('day');
  let descriptions: string[] = [];
  const padStart = (num: number) => num.toString().padStart(2, '0');
  while (startDate.isSameOrBefore(endDate)) {
    const isStartDay = startDate.format('YYYY-MM-DD') === startString;
    const isEndDay = startDate.format('YYYY-MM-DD') === endString;
    const weekday = startDate.weekday();
    const key = weekday === 0 ? 7 : weekday;
    let hours = targetDaypart[key] || targetDaypart[key.toString()];
    if (isStartDay) {
      hours = hours.filter((hour) => +hour >= +start.format('HH'));
    }
    if (isEndDay) {
      hours = hours.filter((hour) => +hour <= +end.format('HH'));
    }
    const descriptionsOfDate = hours.map((hour) => {
      return `${startDate.year()}-${padStart(startDate.month() + 1)}-${padStart(startDate.date())} ${padStart(+hour)}`;
    });
    descriptions = descriptions.concat(descriptionsOfDate);
    startDate.add(1, 'day');
  }
  return descriptions;
}

// ============ 電子紙的四段式時段 ============

/**
 * 電子紙看板一天只切成四段，每段六小時。
 *
 * 這不是 UI 的偏好而是投遞端的硬限制：一台裝置的排程最多只能引用五個不重複的
 * playlist，我們把額度切成「今天晚上 + 明天四段」，所以一天就只有這四個格子。
 * 端點刻意是 `:59` 而不是下一段的 `:00`，廠商那邊沒有明講區間端點含不含，
 * 錯開就不必賭。
 */
export const DAYPART_SEGMENTS = [
  { key: 'h00', hours: [0, 1, 2, 3, 4, 5] },
  { key: 'h06', hours: [6, 7, 8, 9, 10, 11] },
  { key: 'h12', hours: [12, 13, 14, 15, 16, 17] },
  { key: 'h18', hours: [18, 19, 20, 21, 22, 23] }
] as const satisfies ReadonlyArray<{
  key: string;
  hours: ReadonlyArray<DaypartsHour>;
}>;

export type DaypartSegmentKey = (typeof DAYPART_SEGMENTS)[number]['key'];

/**
 * 把選到的時段展開成後端要的「星期 × 小時」格式。
 *
 * 四段式沒有分星期的概念，所以七天一律套同一組小時。
 */
export function segmentsToDayparts(keys: DaypartSegmentKey[]): Dayparts {
  const hours = DAYPART_SEGMENTS.filter(
    (segment) => keys.indexOf(segment.key) !== -1
  )
    .reduce<DaypartsHour[]>((acc, segment) => acc.concat(segment.hours), [])
    .sort((a, b) => a - b);

  return days.reduce((acc, day) => ({ ...acc, [day]: [...hours] }), {
    enabled: true
  } as Dayparts);
}

/**
 * 反推目前選了哪幾段，讓編輯既有活動時下拉能回填。
 *
 * 「該段的任一小時有被選到」就算整段選取——這是刻意與投遞端對齊：後端投影
 * daypart 到四段格子時用的就是同一條規則（任一命中即整段生效）。若這裡改用
 * 「整段小時都要齊」，畫面會顯示沒選，實際卻照投，比不準更難查。
 *
 * 會出現不對齊的小時集合，是因為同一個 dayPart 欄位以前存過自由選取的資料，
 * 或活動被換過銷售渠道。
 */
export function daypartsToSegments(
  dayPart?: Partial<Dayparts>
): DaypartSegmentKey[] {
  if (!dayPart) {
    return [];
  }
  const selectedHours = new Set<number>();
  days.forEach((day) => {
    (dayPart[day] ?? []).forEach((hour) => selectedHours.add(+hour));
  });

  return DAYPART_SEGMENTS.filter((segment) =>
    segment.hours.some((hour) => selectedHours.has(hour))
  ).map((segment) => segment.key);
}
